/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One outcome — _not on the wire_ — and the condition under which it holds** (`P1` slice 3,
 * `MAPPING_EDITOR_UX_GUIDANCE` pattern 24).
 *
 * Ratified by Todd 2026-08-27, rejecting the framing this replaced:
 *
 * > _"`Suppress` means the same thing in impact to the author: **don't put this on the wire.** What
 * > differs is **when that is true.** The global checkbox means 'I don't ever want it to go on the
 * > wire'; the others mean 'if this condition is true, don't put this on the wire': no resolution,
 * > result of an expression, round overrides, or other potential rules we may find like supply
 * > source. The concept is the same to the author."_
 *
 * `suppress` and `optional` were never two concepts and neither is an answer of its own.
 * `optional` is *not on the wire* under the condition **it did not resolve**; the checkbox is the
 * same outcome under the condition **always**. That is why they sit at the same level in the UI —
 * both are unconditional to author — and why everything else (an expression's suppress result, a
 * round override, a supply-source rule) states a **condition** and is therefore a mapping, needing
 * no control of its own.
 *
 * ## The condition slot is open, deliberately
 *
 * Todd's list ends *"or other potential rules we may find like supply source"*, which is the
 * corollary's test asked and answered before the build: **the second instance is already here and
 * the third is named** (round overrides carry a suppression today; supply source is #826). An enum
 * the callers branch on would encode *what we know* rather than *what we know so far*, and every new
 * rule would be a change to two hand-kept lists rather than an addition.
 *
 * So: **nothing outside this file may branch on `when`.** Callers ask {@link omittedBy} and render
 * {@link describeOmission}. `P2 · Criteria` then widens the union as an addition, exactly where
 * `D35` said it would, and no caller changes.
 *
 * ## What this is not
 *
 * It does **not** decide what an unresolved value that is *not* omitted looks like — `foo=` on a
 * query string, absent on a header, key omitted in an ORTB body. That is the surface's business and
 * genuinely varies, which is `MappingControl`'s `whenUnresolved` capability and stays there.
 *
 * It also does not compete with the default. A `sendInstead` value **prevents** the `unresolved`
 * condition from ever firing, because the mapping then resolves — to the default. One condition is
 * not weighed against another; the value simply exists.
 */

/**
 * One reason a mapping is not put on the wire.
 *
 * **Do not `switch` on this outside this module.** See the class comment: the vocabulary is
 * `P2 · Criteria`'s and is expected to grow, and the whole value of the slot is that growing it is
 * an addition.
 */
export type OmissionCondition =
  /**
   * Always — whatever resolves, this never goes out. The row's own checkbox, and today's
   * `suppress: true`.
   *
   * It **dominates** every other condition rather than conflicting with one, because *always* is a
   * superset of any narrower condition. That is what dissolves the apparent contradiction between a
   * suppression and a default: the default is simply never reached.
   */
  | { readonly when: 'always' }
  /**
   * Its mapping chose not to send it, **on this request** — a conditional whose chosen branch is
   * *Never send it*, or a suppress anywhere the value line can hold one. The *"result of an
   * expression"* case in Todd's list above, added 2026-09-16 (frame `0.1` slice `1a`) when the
   * parameter path was found sending `foo=` for it.
   *
   * A **decision**, like `always`: the operator authored the suppress, so it strips a value the
   * driver built procedurally at the same path, and it holds whether or not the row is optional.
   */
  | { readonly when: 'mapping' }
  /**
   * It resolved to nothing — and nothing said what to send instead. Today's `optional: true`.
   *
   * Its absence is not *send nothing anyway*: a mapping that may **not** be absent falls through to
   * the surface's own answer for an unresolved value, which for a query parameter is `foo=`.
   */
  | { readonly when: 'unresolved' };

/** What a mapping declares about not being sent. Empty means: always sent. */
export interface MappingOmission {
  readonly omitWhen: readonly OmissionCondition[];
}

/** Nothing keeps this off the wire. */
export const ALWAYS_SENT: MappingOmission = { omitWhen: [] };

/**
 * **The one reader of a pre-unification row** — the same role `mapping-value-slots.ts` plays for
 * the value slots that `N11f` split (slice 1).
 *
 * `optional` defaults to **true** where a row does not say, which is the reading both drivers
 * already had (`entry.optional !== false`) and must not change: a row that never stated an answer
 * has always been allowed to be absent, and flipping that would start sending `foo=` for every
 * unstated row on every VAST request.
 */
export function omissionOf(row: { optional?: boolean; suppress?: boolean } | undefined): MappingOmission {
  if (!row) return ALWAYS_SENT;

  const omitWhen: OmissionCondition[] = [];
  if (row.suppress === true) omitWhen.push({ when: 'always' });
  if (row.optional !== false) omitWhen.push({ when: 'unresolved' });
  return { omitWhen };
}

/** What a caller knows at the moment it asks. */
export interface OmissionFacts {
  /** Did the mapping produce a value — **after** any default was consulted. */
  readonly resolved: boolean;
  /** Did the mapping itself say *not on the wire* for this request. */
  readonly suppressed?: boolean;
}

const SUPPRESSED_BY_MAPPING: OmissionCondition = { when: 'mapping' };

/**
 * Which condition keeps this off the wire, or `undefined` if none does.
 *
 * Returns the **condition rather than a boolean** so the caller can say *why* without knowing the
 * vocabulary — naming a mechanism is not describing an effect (#1045), and three call sites in the
 * VAST path used to answer this question separately and describe it in three different ways.
 */
export function omittedBy(omission: MappingOmission, facts: OmissionFacts): OmissionCondition | undefined {
  // `always` first: it is a superset, so a row that is both suppressed and unresolved is reported
  // as suppressed — which is the reason the operator would act on.
  const always = omittedUnconditionally(omission);
  if (always) return always;

  // Then the mapping's own decision, which holds whether or not the row may be absent.
  if (facts.suppressed) return SUPPRESSED_BY_MAPPING;

  if (!facts.resolved) return omission.omitWhen.find((c) => c.when === 'unresolved');
  return undefined;
}

/**
 * The condition that keeps this off the wire **whatever it resolves to**, if there is one.
 *
 * Separate from {@link omittedBy} because it is answerable *before* resolving, and resolving a
 * mapping whose result cannot reach the wire is work with a side effect nobody asked for — the
 * driver consults `fallbackResolver` and a macro engine to get there.
 */
export function omittedUnconditionally(omission: MappingOmission): OmissionCondition | undefined {
  return omission.omitWhen.find((c) => c.when === 'always');
}

/**
 * An operator-facing sentence for why a mapping was left off, naming the field.
 *
 * The outcome, not the mechanism: *"suppressed by effective field config"* named an internal
 * structure the operator has never seen.
 */
export function describeOmission(condition: OmissionCondition, fieldName: string): string {
  switch (condition.when) {
    case 'always':
      return `'${fieldName}' is set never to be sent, so it was left off this request.`;
    case 'unresolved':
      return `'${fieldName}' resolved to nothing and has no default, so it was left off this request.`;
    case 'mapping':
      return `'${fieldName}' is set not to be sent in this case, so it was left off this request.`;
  }
}

/**
 * Whether this omission also **strips a value the driver built procedurally** at the same path.
 *
 * The one distinction a caller legitimately needs between conditions, asked in the caller's terms
 * so the *no branching on `when` outside this file* rule stays real. It is CLAUDE.md's existing
 * macro-resolution rule, which had no name: *an explicit `suppress` strips the driver's
 * procedurally-built value at that path, while an unresolved macro must leave it in place* —
 * merging the two would make a typo'd macro silently delete a driver-derived field.
 *
 * The test is whether the condition is a **decision** or a **circumstance**. A new condition
 * answers it by saying which it is; nothing else changes.
 */
export function stripsProceduralValue(condition: OmissionCondition): boolean {
  switch (condition.when) {
    // The operator said never. That is a decision, and it is about the field rather than about
    // this request, so whatever the driver built at that path goes too.
    case 'always':
      return true;
    // Also a decision — the operator authored the branch that suppresses — scoped to this request.
    case 'mapping':
      return true;
    // A circumstance. The driver's own value is not what failed to resolve.
    case 'unresolved':
      return false;
  }
}

/* ------------------------------------------------------------------------- *
 * Authoring the two unconditional conditions
 *
 * An authoring surface has to *name* the conditions an operator sets directly, which is not the
 * same act as branching on `when` to decide behaviour — the thing the class comment prohibits. The
 * two below are the ones with a control; everything else is a mapping and is authored as one.
 * ------------------------------------------------------------------------- */

/** *I never want this on the wire.* The row's own checkbox. */
export const NEVER_SENT: OmissionCondition = { when: 'always' };

/** *This may be absent.* `optional`, keeping its name because it is already the author's word. */
export const OMIT_WHEN_UNRESOLVED: OmissionCondition = { when: 'unresolved' };

/* ------------------------------------------------------------------------- *
 * The named answers an authoring surface offers (`X29`, `#1222`)
 *
 * `X29`: *"one control with named answers makes one of them obviously the answer"*, replacing two
 * checkboxes an operator had to combine in their head. The vocabulary lives here rather than in the
 * console for the reason the class comment gives — the set of conditions is this file's, and an
 * answer is a name for one of them.
 *
 * **The answers are mutually exclusive and the conditions are not**, which is deliberate rather
 * than a lossy mapping: `always` already **dominates** `unresolved` in {@link omittedBy}, so
 * `[always, unresolved]` and `[always]` are the same behaviour and a control that could express
 * both would be offering the operator a distinction the driver does not make.
 * ------------------------------------------------------------------------- */

/**
 * One answer to *is this on the wire*.
 *
 * **`whenUnresolved` is not spelled `optional`** (`X46`): that word also named the destination's
 * required-ness claim, which alters nothing on the wire and now lives on `DestinationField.importance`.
 *
 * The union is expected to grow — `X29`'s *omit it when…* takes a Criteria (`X7a`). It grew once
 * already: `X33`'s **`inherit`** arrived with `XS3` slice 5, exactly as this paragraph predicted, as
 * an addition here and a new option in the control rather than a change to two booleans.
 *
 * ## `inherit` is the absence of a declaration, not a fourth condition
 *
 * `X33`: *"an override's fourth answer is to decline … it is not a new mechanism, it is the absence
 * of an override."* So it deliberately does **not** join {@link OmissionCondition} — there is no
 * reason-this-was-omitted to record, because this layer said nothing and the layer beneath answered.
 * Its representation is `null` in {@link omissionAnswerOf} / {@link omissionForAnswer}, which is why
 * those two take and return `MappingOmission | null`.
 *
 * **The runtime needed nothing for it**, and that is the evidence it is an absence rather than a
 * mechanism: `BaseDriver.getEffectiveParamFieldConfigs` merges a round override field by field, so
 * an override entry declaring no value, no mapping, no expression and no suppression already leaves
 * the source's own row exactly as it was. Asserted in `demand-source-driver.round-override.spec.ts`
 * rather than argued from reading it.
 *
 * **Only a layering surface may offer it.** A surface with nothing beneath it has no value to fall
 * back to, so the answer would name a mechanism that does not exist there (#1045) — which is what
 * `MappingSurfaceCapabilities.declaresOmission.layering` declares, per surface.
 */
export type OmissionAnswer = 'always' | 'whenUnresolved' | 'never' | 'inherit';

/**
 * Which answer this mapping represents.
 *
 * `never` wins where both are declared, because that is what the driver does — reading it any other
 * way would show an operator a state their requests are not in.
 *
 * **`null` is a layer that declares nothing** (`X33`) — not *always sent*, which is a claim. A
 * surface that always declares something simply never passes it, and its call sites are unchanged.
 */
export function omissionAnswerOf(omission: MappingOmission | null): OmissionAnswer {
  if (omission === null) return 'inherit';
  if (isDeclared(omission, NEVER_SENT)) return 'never';
  if (isDeclared(omission, OMIT_WHEN_UNRESOLVED)) return 'whenUnresolved';
  return 'always';
}

/**
 * The mapping an answer means — **exactly one condition set, never a patch of the previous one.**
 *
 * A setter that toggled one condition and left the other would let the control produce
 * `[always, unresolved]`, which is a state it cannot render back. The pair with
 * {@link omissionAnswerOf} is the round trip, for the reason `#1128` gives: a surface that reads
 * through one function and writes through its own is how the two come to disagree.
 */
export function omissionForAnswer(answer: OmissionAnswer): MappingOmission | null {
  switch (answer) {
    case 'never':
      return { omitWhen: [NEVER_SENT] };
    case 'whenUnresolved':
      return { omitWhen: [OMIT_WHEN_UNRESOLVED] };
    case 'always':
      return ALWAYS_SENT;
    // Nothing to declare. The caller is a layering surface — it is the one that knows what
    // *nothing* resolves to there, and the one that has somewhere to store the absence.
    case 'inherit':
      return null;
  }
}

/** Whether a mapping declares this condition. */
export function isDeclared(omission: MappingOmission, condition: OmissionCondition): boolean {
  return omission.omitWhen.some((c) => c.when === condition.when);
}

/**
 * The mapping with this condition added or removed — **the write half of `omissionOf`'s read.**
 *
 * A pair rather than a setter per condition, because *share the round trip* (#1128) applies to a
 * declaration as much as to a value: a surface that reads through one function and writes through
 * its own is how the two come to disagree about what an absent entry means.
 */
export function withDeclared(
  omission: MappingOmission,
  condition: OmissionCondition,
  declared: boolean,
): MappingOmission {
  const without = omission.omitWhen.filter((c) => c.when !== condition.when);
  return { omitWhen: declared ? [...without, condition] : without };
}

/**
 * The row booleans this mapping implies, for a store that still keeps two of them.
 *
 * Deliberately **lossy in one direction and honest about it**: a condition that is neither of the
 * two unconditional ones has no boolean to be written to, so it cannot round-trip through a
 * pre-unification row. That is not a gap to fill — those conditions are mappings, and a mapping is
 * stored as a mapping.
 */
export function omissionToRow(omission: MappingOmission): { optional: boolean; suppress: boolean } {
  return {
    optional: isDeclared(omission, OMIT_WHEN_UNRESOLVED),
    suppress: isDeclared(omission, NEVER_SENT),
  };
}
